import React, { useEffect, useMemo, useState } from 'react';
import './Rewards.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { getLeaderboard, getRewardRules, getUserEarnings } from '../../api/rewards';
import { getToken, getUserId } from '../../api/index';

const Rewards = () => {
    // const navigate = useNavigate();
    const [howOpen, setHowOpen] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [apiTotalPoints, setApiTotalPoints] = useState(0);
    const [rules, setRules] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const userDetails = useSelector((state) => state.userDetails.userDetails);

    // Derive completion states similar to ProfileCompleteness
    const governmentIdStatus = useMemo(() => {
        if (!userDetails?.governmentId) return { isUploaded: false, isVerified: false, isComplete: false };
        const isUploaded = Boolean(userDetails.governmentId.id);
        const isVerified = userDetails.governmentId.verificationStatus === 'approved';
        return { isUploaded, isVerified, isComplete: isUploaded && isVerified };
    }, [userDetails]);

    // Heuristic for subscription purchase from user profile
    const subscriptionComplete = Boolean(
        userDetails?.subscription?.isActive || userDetails?.isSubscribed || userDetails?.plan
    );

    // Catalog of rewardable steps (can be extended later)
    const rewardSteps = useMemo(() => ([
        {
            id: 'subscription',
            title: 'Subscription Purchase',
            points: 150,
            completed: subscriptionComplete,
        },
        {
            id: 'govt-id',
            title: 'Government Id Verification',
            points: 100,
            completed: governmentIdStatus.isComplete,
        },
    ]), [subscriptionComplete, governmentIdStatus.isComplete]);

    // Prefer server-provided points if available; fall back to heuristic
    const computedPoints = rewardSteps
        .filter((s) => s.completed)
        .reduce((sum, s) => sum + s.points, 0);
    const totalPoints = apiTotalPoints || computedPoints;

    const currentUserName = `${userDetails?.firstName || 'Anonymous'} ${userDetails?.lastName || ''}`.trim();

    // Normalization helpers
    const extractTotalPoints = (response) => {
        if (!response) return 0;
        return Number(
            response?.data?.totalPoints ??
            response?.totalPoints ??
            response?.points ??
            0
        ) || 0;
    };

    const normalizeRules = (response) => {
        const payload = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
                ? response
                : [];
        return payload.map((r, idx) => ({
            id: r.rewardRuleId || r.id || r.ruleId || `rule-${idx}`,
            actionType: r.actionType || r.title || r.name || r.ruleName || 'Rule',
            points: r.points || r.rewardPoints || r.score || 0,
            description: r.description || '',
        }));
    };

    const normalizeLeaderboard = (response) => {
        const payload = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
                ? response
                : [];
        return payload
            .map((p, idx) => ({
                name: p.name || p.userName || p.fullName || `User ${idx + 1}`,
                points: p.points || p.totalPoints || p.score || 0,
            }))
            .sort((a, b) => b.points - a.points);
    };

    const topThree = leaderboard.slice(0, 3);
    // Reorder for podium so center is rank 1: [2nd, 1st, 3rd]
    const podium = topThree.length === 3 ? [topThree[1], topThree[0], topThree[2]] : topThree;
    const others = leaderboard.slice(3);
    const userRank = leaderboard.findIndex(p => p.name.toLowerCase() === currentUserName.toLowerCase()) + 1;

    // Show a short confetti burst if user is in top 3
    useEffect(() => {
        if (userRank > 0 && userRank <= 3) {
            setShowConfetti(true);
            const t = setTimeout(() => setShowConfetti(false), 2200);
            return () => clearTimeout(t);
        }
    }, [userRank]);

    // Fetch Rewards data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');
                const token = getToken();
                const userId = getUserId();
                if (!token) {
                    setError('Not authenticated');
                    setLoading(false);
                    return;
                }

                const [earningsRes, rulesRes, leaderboardRes] = await Promise.all([
                    userId ? getUserEarnings(userId, token) : Promise.resolve(null),
                    getRewardRules(token),
                    getLeaderboard(token),
                ]);

                // Earnings
                if (earningsRes) {
                    setApiTotalPoints(extractTotalPoints(earningsRes));
                }

                // Rules
                setRules(normalizeRules(rulesRes));

                // Leaderboard with current user ensured
                const normalizedLb = normalizeLeaderboard(leaderboardRes);
                const hasCurrentUser = normalizedLb.some(b => b.name.toLowerCase() === currentUserName.toLowerCase());
                const withCurrentUser = hasCurrentUser
                    ? normalizedLb
                    : [{ name: currentUserName, points: totalPoints }, ...normalizedLb];
                setLeaderboard(withCurrentUser);
            } catch (e) {
                setError(e?.response?.data?.message || e.message || 'Failed to load rewards');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="profileCompleteness">
            <div className="rewardsContainer">
                <div className="rewardsHeader">
                    <div className="rewardsTrophy">
                        <EmojiEventsIcon className="trophyIcon" />
                    </div>
                    <div className="rewardsTitleBlock">
                        <h1 className="rewardsTitle">Rewards</h1>
                        <p className="rewardsSubtitle">Track your points and complete tasks to earn more</p>
                    </div>
                </div>

                {/* Points Card */}
                <div className="pointsCard">
                    <div className="pointsContent">
                        <span className="pointsLabel">Your Points</span>
                        <div className="pointsValue">{totalPoints}</div>
                    </div>
                    <div className="pointsBadge">
                        {/* purely decorative */}
                        <EmojiEventsIcon className="pointsBadgeIcon" />
                    </div>
                </div>

                {loading && (
                    <div className="sectionCard"><div className="emptyState">Loading rewards…</div></div>
                )}
                {!!error && (
                    <div className="sectionCard"><div className="emptyState">{error}</div></div>
                )}

                {/* Completed Section */}
                <div className="sectionCard">
                    <div className="sectionHeader">Completed</div>
                    <div className="completedList">
                        {rewardSteps.filter((s) => s.completed).length === 0 && (
                            <div className="emptyState">No completed steps yet. Start earning now!</div>
                        )}
                        {rewardSteps.filter((s) => s.completed).map((step) => (
                            <motion.div key={step.id} className="completedItem" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                                <div className="completedIconWrap">
                                    <CheckCircleIcon className="completedIcon" />
                                </div>
                                <div className="completedText">
                                    <div className="completedTitle">{step.title}</div>
                                </div>
                                <div className="pointsTag">+{step.points}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* How to earn (collapsible) */}
                <div className="sectionCard clickable" onClick={() => setHowOpen((v) => !v)}>
                    <div className="howToEarn">
                        <div className="howLeft">
                            <div className="howIcon">
                                <EmojiEventsIcon className="howIconSvg" />
                            </div>
                            <div className="howText">
                                <div className="howTitle">See all ways to earn points</div>
                                <div className="howSubtitle">View reward rules and points</div>
                            </div>
                        </div>
                        <ChevronRightIcon className={howOpen ? "chevronIcon chevronOpen" : "chevronIcon"} />
                    </div>
                    {howOpen && (
                        <div className="howContent">
                            {rules.length === 0 && (
                                <div className="emptyState">No reward rules available.</div>
                            )}
                            {rules.map((rule) => (
                                <div key={rule.id} className="howRow">
                                    <span className="howRowTitle">{rule.actionType}</span>
                                    <span className="howRowPoints">+{rule.points}</span>
                                </div>
                            ))}
                            {!subscriptionComplete && (
                                <div className="howHint">Complete your subscription to earn points.</div>
                            )}
                            {!governmentIdStatus.isComplete && (
                                <div className="howHint">Verify your government ID to earn points.</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Leaderboard */}
                <div className="sectionCard">
                    <div className="sectionHeader">Leaderboard</div>

                    <div className="leaderboardPodium">
                        <AnimatePresence>
                            {podium.map((p, idx) => {
                                const isCenter = idx === 1; // second item visually center for 1st place
                                return (
                                    <motion.div
                                        key={p.name}
                                        className={isCenter ? 'podiumItem center' : 'podiumItem'}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        whileHover={{ scale: 1.02 }}
                                    >
                                        <div className={isCenter ? 'avatar crown' : 'avatar'}>
                                            <span className="avatarInitial">{p.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                        <div className="podiumName">{p.name}</div>
                                        <div className="podiumPoints">{p.points}</div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>

                    <div className="leaderboardList">
                        {others.map((p, idx) => (
                            <motion.div key={p.name} className="leaderItem" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: idx * 0.02 }}>
                                <div className="leaderLeft">
                                    <div className="rank">#{idx + 4}</div>
                                    <div className="smallAvatar"><span>{p.name.charAt(0).toUpperCase()}</span></div>
                                    <div className="leaderName">{p.name}</div>
                                </div>
                                <div className="leaderPoints">{p.points}</div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Confetti celebration for top 3 */}
                    <AnimatePresence>
                        {showConfetti && (
                            <Confetti
                                recycle={false}
                                numberOfPieces={220}
                                gravity={0.3}
                                tweenDuration={1200}
                                width={window.innerWidth}
                                height={window.innerHeight}
                            />
                        )}
                    </AnimatePresence>
                    {userRank > 0 && (
                        <div className="userRankHint">Your current rank: #{userRank}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Rewards;